import { inject, Injectable } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import type { StepParams } from '@enlighten/ts-shared/step-api';

import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import { ExecJob, ExecPlan, JobSource, TriggerRoutineRequest } from '../../interfaces';
import { AppStateService } from '../app-state/app-state.service';
import { ExecutionPlanService } from '../execution-plan/execution-plan.service';
import { ObjectStorageFileService } from '../object-storage/object-storage-file.service';
import { CacheService } from '../cache/cache.service';
import { FileDataService } from '../file-data/file-data.service';
import { MessageService } from '../message/message.service';
import { RELOAD_NOTEBOOK_FILES } from '../../constants/general.constants';
import { GeneralHelpers } from '../../helpers/general.helper';
import { ensureParentDirs } from '../../helpers/emscripten-fs.helper';
import { PYTHON_DIRECTORIES } from '../python/interfaces/python.interface';

/** Above this size, skip notebook cache sync to avoid a second full-file buffer (SCRUM-333). */
const MAX_BYTES_FOR_CACHE_SYNC = 32 * 1024 * 1024;


/**
 * RoutineService — routine / job helpers callable from Python and JavaScript steps.
 *
 * SCRUM-259: Where nc-server exposes an HTTP API, call it from the browser; for
 * headless-only job control, warn and no-op so workflows stay debuggable.
 */
@Injectable({
  providedIn: 'root',
})
export class RoutineService {
  private readonly appState = inject(AppStateService);
  private readonly executionPlanService = inject(ExecutionPlanService);
  private readonly objectStorageFileService = inject(ObjectStorageFileService);
  private readonly cacheService = inject(CacheService);
  private readonly fileDataService = inject(FileDataService);
  private readonly messageService = inject(MessageService);

  private pyodideFS: any = null;

  /**
   * Set Pyodide FS reference so downloaded files are written to the in-memory
   * filesystem for immediate access within the same step (matches BoxService).
   */
  setPyodideFS(fs: any): void {
    this.pyodideFS = fs;
  }

  /**
   * Request a retry of the current job — halts execution immediately.
   *
   * Parity with enoch's RoutineService.requestRetry (which sets
   * globalThis._retryRequested and throws RetryRequestedError): this MUST stop
   * the step rather than warn-and-continue, otherwise code after requestRetry()
   * keeps running (SCRUM-718). The flag lets the step executor render a clean
   * "Retry requested: <msg>" outcome instead of a red execution error — a
   * designed control-halt is not a crash. See abortJob for why a global flag is
   * used (survives the Pyodide marshaling boundary; a thrown class identity does
   * not).
   *
   * The parser trims the message and applies the default (EHQ-10).
   */
  requestRetry(params: StepParams['EHQ.requestRetry'], _context?: ChunkContext, _runtime?: string): never {
    const { message } = params;
    (globalThis as any)._retryRequested = message;
    throw new Error(message);
  }

  /**
   * Abort the current job — halts execution immediately.
   *
   * Parity with enoch's RoutineService.abortJob (which sets the job status to
   * Aborted and throws JobAbortedError): abortJob MUST stop the step. The old
   * warn-and-continue stub let code after abortJob() keep running — e.g. a
   * diagnose step that called abortJob() still fell through to the API call
   * after it (SCRUM-718). Throwing halts the step exactly as headless execution
   * does.
   *
   * Before throwing we flag the controlled abort on `globalThis._abortRequested`
   * (mirroring enoch, which detects the abort via this same global). The step
   * executor reads the flag to render a clean "Job aborted: <msg>" outcome
   * instead of a red "Execution error: ehq.abortJob() failed" — a designed abort
   * is not a crash. The flag is used rather than a JobAbortedError subclass
   * because a thrown class identity does not reliably survive the Pyodide
   * marshaling boundary, whereas the global does (this is why enoch uses it too).
   *
   * The parser trims the message and applies the default (EHQ-10).
   */
  abortJob(params: StepParams['EHQ.abortJob'], _context?: ChunkContext, _runtime?: string): never {
    const { message } = params;
    (globalThis as any)._abortRequested = message;
    throw new Error(message);
  }

  /**
   * Request operator review of the current job — halts execution immediately.
   *
   * Parity with enoch's RoutineService.requestReview (which sets
   * globalThis._reviewRequested and throws ReviewRequestedError). Like
   * abortJob/requestRetry, throwing halts the step exactly as headless execution
   * does, and the flag lets the executor render a clean "Review requested: <msg>"
   * outcome instead of a red execution error (SCRUM-718).
   *
   * The optional fileNames argument (EHQ-134) is normalized by the shared
   * parser, so an author gets the same feedback in the browser as headless,
   * but it has no effect in preview: resolving names to files needs a
   * persisted job, and a preview run never creates one. Saying so in the step
   * output is better than silently swallowing it and leaving the author to
   * wonder. An unusable list never fails the review: the parser drops it and
   * reports why in `fileNamesError`.
   */
  requestReview(params: StepParams['EHQ.requestReview'], context?: ChunkContext, _runtime?: string): never {
    const { message, fileNames, fileNamesError } = params;
    (globalThis as any)._reviewRequested = message;

    if (fileNamesError !== undefined) {
      context?.addMessage(
        `requestReview: ignoring an unusable file list (${fileNamesError}); the review will offer all of the job's files.`,
        'warning',
      );
    }

    if (fileNames && context) {
      context.addMessage(
        `requestReview: ${fileNames.length} review ${
          fileNames.length === 1 ? 'file' : 'files'
        } named (${fileNames.join(', ')}). These are recorded only when the workflow runs as a job — in preview they are ignored.`,
        'warning',
      );
    }

    throw new Error(message);
  }

  /**
   * The routine a name refers to, or null when the project has none by that
   * name (a 404). Other HTTP failures propagate.
   */
  private async findRoutineByName(projectId: number, name: string): Promise<ExecPlan | null> {
    return await firstValueFrom(
      this.executionPlanService.getPlanByRoutineName(projectId, name).pipe(
        catchError((err: { status?: number }) => {
          if (err?.status === 404) {
            return of(null);
          }
          throw err;
        })
      )
    );
  }

  /** The active project's id; throws, and reports it on the step, when there is none. */
  private requireProjectId(method: string, context?: ChunkContext): number {
    const projectId = this.appState.projectId();
    if (!projectId) {
      const msg = `${method}: no active project`;
      if (context) {
        context.addMessage(msg, 'danger');
      }
      throw new Error(msg);
    }
    return projectId;
  }

  /**
   * Get routine metadata by name. A routine that doesn't exist returns null.
   */
  async getRoutineInfo(params: StepParams['EHQ.getRoutineInfo'], context?: ChunkContext): Promise<ExecPlan | null> {
    const projectId = this.requireProjectId('getRoutineInfo', context);
    try {
      return await this.findRoutineByName(projectId, params.name);
    } catch (error: unknown) {
      const msg = `Error in getRoutineInfo: ${error instanceof Error ? error.message : String(error)}`;
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw error;
    }
  }

  /**
   * Pause or resume a routine, by name or integer id. A name is resolved to
   * its id first, as enoch does; an unknown name throws.
   */
  async setRoutinePaused(params: StepParams['EHQ.setRoutinePaused'], context?: ChunkContext): Promise<void> {
    const projectId = this.requireProjectId('setRoutinePaused', context);
    let routineId: number;
    if (typeof params.routine === 'number') {
      routineId = params.routine;
    } else {
      const plan = await this.findRoutineByName(projectId, params.routine);
      if (!plan) {
        const msg = `Routine "${params.routine}" not found`;
        if (context) {
          context.addMessage(msg, 'danger');
          context.setChunkResultStatus('error');
        }
        throw new Error(msg);
      }
      routineId = plan.routineId;
    }
    await firstValueFrom(
      this.executionPlanService.setPlanPaused(projectId, routineId, params.paused)
    );
  }

  /**
   * Queue a routine run. Actual execution happens in enoch for scheduled/headless runs.
   *
   * The request body is built as enoch builds it: `source: 'triggered'`, and
   * the job arguments as a string (passed as is, or JSON-encoded).
   */
  async triggerRoutine(params: StepParams['EHQ.triggerRoutine'], context?: ChunkContext): Promise<ExecJob | null> {
    console.warn('[RoutineService] Routine triggered — will execute in enoch (headless).');
    if (context) {
      context.addMessage(
        'Routine triggered — will execute in enoch (headless executor).',
        'warning'
      );
    }

    const projectId = this.requireProjectId('triggerRoutine', context);

    let routineId: number;
    if (typeof params.routine === 'number') {
      routineId = params.routine;
    } else {
      const plan = await this.findRoutineByName(projectId, params.routine);
      if (!plan) {
        const msg = `No routine named "${params.routine}"`;
        if (context) {
          context.addMessage(msg, 'danger');
        }
        return null;
      }
      routineId = plan.routineId;
    }

    return await firstValueFrom(
      this.executionPlanService.triggerPlan(projectId, routineId, RoutineService.triggerBody(params.jobArgs))
    );
  }

  /** enoch's trigger body: falsy job arguments are left out, as enoch's truthiness check does. */
  private static triggerBody(jobArgs: unknown): TriggerRoutineRequest {
    const body: TriggerRoutineRequest = { source: JobSource.Triggered };
    if (jobArgs) {
      body.arguments = typeof jobArgs === 'string' ? jobArgs : JSON.stringify(jobArgs);
    }
    return body;
  }

  /**
   * Download an object storage file into the workflow workspace using streaming I/O (SCRUM-333).
   * Requires an active Arrow Flight WebSocket (ARROW_FLIGHT.connect) for presigning.
   *
   * The parser checks the arguments (EHQ-10), so a malformed call now fails
   * even outside Pyodide, before the skip below.
   */
  async downloadFromStorage(params: StepParams['EHQ.downloadFromStorage'], context?: ChunkContext): Promise<void> {
    const skipMsg =
      'downloadFromStorage skipped — not running in Pyodide workflow context (file is available during routine execution in enoch)';
    if (!this.pyodideFS) {
      console.warn(`[RoutineService] ${skipMsg}`);
      if (context) {
        context.addMessage(skipMsg, 'warning');
      }
      return;
    }

    const { connectionName, objectPath, workflowFileName, durationSeconds } = params;

    try {
      // Presigned read (Get) URL via ehq-server's HTTP signed-url endpoint
      // (EHQ-5) — no Arrow Flight session required.
      const presignedUrl = await this.objectStorageFileService.presignUrl({
        connectionName,
        path: objectPath,
        expirySecs: durationSeconds,
        method: 'Get',
      });

      if (!presignedUrl || typeof presignedUrl !== 'string') {
        throw new Error('Presign did not return a URL');
      }

      const response = await fetch(presignedUrl, { credentials: 'omit' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const fs = this.pyodideFS;
      const filesDir = `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}`;
      try {
        fs.mkdirTree(filesDir);
      } catch {
        // Directory may already exist
      }
      const safeRelative = RoutineService.sanitizeWorkflowRelativePath(workflowFileName);
      const fullPath = `${filesDir}/${safeRelative}`;

      const totalBytes = await RoutineService.streamHttpBodyToEmscriptenFs(response, fs, fullPath);

      const mb = (totalBytes / (1024 * 1024)).toFixed(1);
      if (context) {
        context.addMessage(
          `Streamed ${mb} MB from storage to ${safeRelative} (chunked download, SCRUM-333)`,
          'success'
        );
      }

      const shouldSyncCache =
        totalBytes > 0 && totalBytes <= MAX_BYTES_FOR_CACHE_SYNC;

      if (shouldSyncCache) {
        await this.syncNotebookCacheFromPyodideFile(
          safeRelative,
          fullPath,
          totalBytes,
          context
        );
      } else if (context && totalBytes > MAX_BYTES_FOR_CACHE_SYNC) {
        context.addMessage(
          'Large download: file is on the Python workflow filesystem; notebook cache sync skipped to save memory.',
          'warning'
        );
      }

      this.messageService.sendMessage(RELOAD_NOTEBOOK_FILES);
    } catch (error: unknown) {
      const msg = `downloadFromStorage failed: ${error instanceof Error ? error.message : String(error)}`;
      if (context) {
        context.addMessage(msg, 'danger');
        context.setChunkResultStatus('error');
      }
      throw new Error(msg);
    }
  }

  /**
   * Sync a downloaded file into notebook cache (small files only).
   */
  private async syncNotebookCacheFromPyodideFile(
    workflowFileName: string,
    fullPath: string,
    byteLength: number,
    context?: ChunkContext
  ): Promise<void> {
    const fs = this.pyodideFS;
    if (!fs || byteLength <= 0) {
      return;
    }

    let data: Uint8Array;
    try {
      data = fs.readFile(fullPath);
    } catch (e) {
      console.warn('[RoutineService] Could not read file for notebook cache sync:', e);
      return;
    }

    const blob = new Blob([data as BlobPart], { type: 'application/octet-stream' });
    // Stored under the full relative path, like a step's saveFile (EHQ-455).
    const cacheName = GeneralHelpers.cacheFileName(workflowFileName);
    const fileName =
      GeneralHelpers.directoryFromString(workflowFileName) +
      GeneralHelpers.fileNameFromString(workflowFileName);
    const fileExtension = GeneralHelpers.fileExtensionFromString(workflowFileName);
    const formData = this.fileDataService.prepareFileData(fileName, fileExtension, blob);
    formData.append('name', cacheName);

    const cacheContent = await firstValueFrom(this.cacheService.getCacheContent());
    const existingFile = GeneralHelpers.findElementInArrayByProp(
      cacheContent,
      'name',
      cacheName
    );

    if (existingFile) {
      await firstValueFrom(
        this.cacheService.updateCacheContentFile(existingFile['fileId'], formData)
      );
      if (context) {
        context.addMessage(`Updated notebook cache for ${workflowFileName}`, 'success');
      }
    } else {
      await firstValueFrom(this.cacheService.createCacheContent(formData));
      if (context) {
        context.addMessage(`Added ${workflowFileName} to notebook cache`, 'success');
      }
    }
  }

  private static sanitizeWorkflowRelativePath(name: string): string {
    const trimmed = name.trim().replace(/^[/\\]+/, '');
    const noParent = trimmed.split(/[/\\]/).filter((p) => p && p !== '.' && p !== '..').join('/');
    return noParent || 'download.bin';
  }

  /**
   * Ensures parent directories exist for a file path (Emscripten FS), e.g. files/data/out.json.
   */
  private static ensureParentDirsForEmscriptenFile(fs: any, fullPath: string): void {
    ensureParentDirs(fs, fullPath);
  }

  /**
   * Streams an HTTP response body to an Emscripten FS file without buffering the full body in JS.
   */
  private static async streamHttpBodyToEmscriptenFs(
    response: Response,
    fs: any,
    fullPath: string
  ): Promise<number> {
    RoutineService.ensureParentDirsForEmscriptenFile(fs, fullPath);

    const body = response.body;
    if (!body) {
      const buf = await response.arrayBuffer();
      fs.writeFile(fullPath, new Uint8Array(buf));
      return buf.byteLength;
    }

    const reader = body.getReader();
    let stream: any;
    try {
      stream = fs.open(fullPath, 'w');
    } catch (e) {
      throw new Error(`FS.open failed for ${fullPath}: ${e instanceof Error ? e.message : String(e)}`);
    }

    let total = 0;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        if (value && value.byteLength) {
          fs.write(stream, value, 0, value.length, total);
          total += value.length;
        }
      }
    } finally {
      try {
        fs.close(stream);
      } catch {
        // ignore
      }
    }

    return total;
  }
}
